import React, { useEffect } from 'react'
import {
  Button,
  Card,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
} from '@mui/material'
import { ErrorFallback } from 'bizcharts/lib/boundary/ErrorBoundary'
import { ErrorBoundary } from 'react-error-boundary'
import Layout from '~/components/Layout'
import Loading from '~/components/Loading'
import { useLang } from '~/hooks/useLang'
import {
  downloadReportCSV,
  makeRequestToGetReportOccurrencesHandledByGroup,
} from '../../services/report'
import { useHookstate } from '@hookstate/core'
import useReportState from '../../stores/ReportState'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import notify from '~/utils/notify'
import FilterAutocompleteCategory from '~/components/FilterAutocompleteCategory'
import FilterAutocompleteTypes from '~/components/FilterAutoCompleteTypes'
import GenerateReportButton from '../../components/GenerateReportButton'
import FilterSwitchOtherDispatchsGroup from '~/components/FilterSwitchOtherDispatchsGroups'
import ExportButtonCSV from '../../components/ButtonExportCSV'
import FilterAutocompleteCity from '~/components/FilterSelectAutocompleteCity'
import FilterAutocompleteEntryOrigin from '~/components/FilterAutocompleteEntryOrigin'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import FilterAutocompleteOperation from '~/components/FilterAutocompleteOperation'
import FilterTextField from '~/components/FilterTextField'
import FilterAutocompleteDistrict from '~/components/FilterSelectAutocompleteDistrict'
import { generateCSVFileName } from '~/utils/strings'

// eslint-disable-next-line max-lines-per-function
export default function OccurrenceHandledByGroup() {
  const { reportData, loadingReport } = useReportState()
  const { translate } = useLang()
  const isLoading = useHookstate(false)
  const deviceId = useHookstate<number | null>(null)
  const lockFields = useHookstate<boolean>(false)
  const startDate = useHookstate<string | null>(null)
  const finishDate = useHookstate<string | null>(null)
  const period = useHookstate('')
  const groupId = useHookstate<readonly number[]>([])
  const typeId = useHookstate<readonly number[]>([])
  const agencyIds = useHookstate<readonly number[]>([])
  const labelButton = useHookstate('')
  const expandFilterSearch = useHookstate<boolean>(true)
  const categoryId = useHookstate<number | null>(null)
  const withSolvedThirdParty = useHookstate<boolean>(false)
  const cityId = useHookstate<number | null>(null)
  const entryOriginId = useHookstate<number | null>(null)
  const groupSetId = useHookstate<number | null>(null)
  const operationId = useHookstate<number | null>(null)
  const location = useHookstate<string | null>(null)
  const districtId = useHookstate<number | null>(null)
  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  async function generateReport(exportCSV = false) {
    if (startDate.get() && finishDate.get()) {
      loadingReport().set(true)
      isLoading.set(true)

      const data = await makeRequestToGetReportOccurrencesHandledByGroup({
        startDate: startDate.get(),
        finishDate: finishDate.get(),
        dispatchGroupsId: groupId.get(),
        agencyIds: agencyIds.get(),
        categoryId: categoryId.get(),
        typeIds: typeId.get(),
        withSolvedThirdParty: withSolvedThirdParty.get(),
        cityId: cityId.get(),
        entryOriginId: entryOriginId.get(),
        groupSetId: groupSetId.get(),
        location: location.get(),
        operationId: operationId.get(),
        districtId: districtId.get(),
        exportCSV: exportCSV,
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
      })
      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }

      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('Occurrences Handled by Dispatch Group', translate),
          data
        )
      } else {
        reportData().set(data)
        window.open(`/report/result/occurrences-handled-by-group`)
      }
    } else {
      notify({
        message: translate('Initial and final date are mandatory'),
        type: 'error',
      })
    }
  }

  // const exportCSV = async () => {
  //   if (startDate && finishDate) {
  //     isLoading.set(true)
  //     const dataCSV = await makeRequestToGetReportOccurrencesHandledByGroup({
  //       startDate: startDate.get(),
  //       finishDate: finishDate.get(),
  //       dispatchGroupsId: groupId.get(),
  //       agencyIds: agencyIds.get(),
  //       categoryId: categoryId.get(),
  //       typeIds: typeId.get(),
  //       withSolvedThirdParty: withSolvedThirdParty.get(),
  //       exportCSV: true,
  //       cityId: cityId.get(),
  //       entryOriginId: entryOriginId.get(),
  //       groupSetId: groupSetId.get(),
  //       operationId: operationId.get()
  //     }).finally(() => isLoading.set(false))
  //     downloadReportCSV(
  //       `${translate(reportName)}-${new Date().getTime()}.csv`,
  //       dataCSV
  //     )
  //   } else {
  //     notify({
  //       message: translate('Initial and final date are mandatory'),
  //       type: 'error',
  //     })
  //   }
  // }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(
                  `Occurrences Handled by Dispatch Group`
                )}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>

                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteTypes
                    disabled={lockFields.get()}
                    categoryId={categoryId.get() ?? undefined}
                    typeId={typeId.get()}
                    onTypeChange={(type) => {
                      typeId.set(type)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchGroupSet
                    disabled={lockFields.get()}
                    dispatchGroupSetId={groupSetId?.get() ?? undefined}
                    onGroupDispatchSetChange={(group) => {
                      groupSetId?.set(group)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchsGroups
                    disabled={lockFields.get()}
                    agencyId={agencyIds.get() ?? undefined}
                    dispatcheGroups={groupId.get() ?? undefined}
                    onGroupDispatchChange={(group) => {
                      groupId.set(group)
                      deviceId.set(null)
                      labelButton.set(translate('Search'))
                    }}
                    groupSetId={groupSetId.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteCity
                    disabled={lockFields.get()}
                    onCityChange={(city) => {
                      cityId?.set(city)
                      labelButton.set(translate('Search'))
                    }}
                    cityId={cityId?.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDistrict
                    disabled={!cityId?.get()}
                    onDistrictChange={(district) => {
                      districtId?.set(district)
                    }}
                    districtId={districtId}
                    cityId={cityId ? cityId.get() : null}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterTextField
                    currentText={location.get()}
                    label={'Location'}
                    onTextFieldChange={(textField) => {
                      location.set(textField)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteEntryOrigin
                    disabled={lockFields.get()}
                    onEntryOriginChange={(entryOrigin) => {
                      entryOriginId?.set(entryOrigin)
                      labelButton.set(translate('Search'))
                    }}
                    originId={entryOriginId?.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteOperation
                    disabled={lockFields.get()}
                    onOperationChange={(operation) => {
                      operationId.set(operation)
                      labelButton.set(translate('Search'))
                    }}
                    operationId={operationId.get()}
                  />
                </Grid>
                <FilterSelectPeriod
                  disabled={lockFields.get()}
                  onStartDateChange={(value) =>
                    startDate.set(value?.toJSON() ?? null)
                  }
                  onFinishDateChange={(value) =>
                    finishDate.set(value?.toJSON() ?? null)
                  }
                  onPeriodChange={(value) => {
                    period.set(value)
                  }}
                  period={period.get()}
                  required
                />
                <Grid item xs={4} sx={{ pl: 1, pt: 1 }}>
                  <FilterSwitchOtherDispatchsGroup
                    onCheckedChange={() => {
                      labelButton.set(translate('Search'))
                      withSolvedThirdParty.set(!withSolvedThirdParty.value)
                    }}
                    disabled={lockFields.get()}
                    checked={withSolvedThirdParty.get()}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    categoryId.set(null)
                    deviceId.set(null)
                    agencyIds.set([])
                    groupSetId.set(null)
                    groupId.set([])
                    entryOriginId.set(null)
                    cityId.set(null)
                    period.set('')
                    typeId.set([])
                    withSolvedThirdParty.set(false)
                    operationId.set(null)
                    location.set(null)
                    districtId.set(null)
                  }}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={generateReport}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
